"use client";

import { SiteTemplatePageSectionTypeHeadlineDefaultListItemSettingModel } from "@/Apis/ClinicApi";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { HeadlineSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement } from "react";
import HomeEighteenReadySectionGenerated, {
  HomeEighteenReadySectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenReadySection.generated";
import HomeFourteenTreatmentSectionGenerated, {
  HomeFourteenTreatmentSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenTreatmentSection.generated";
import HomeSeventeenPreviewSectionGenerated, {
  HomeSeventeenPreviewSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenPreviewSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedHeadlineRenderer = (props: HeadlineSectionProps) => ReactElement;

const HeadlineAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings
    .SiteTemplateHeadlineAppearance;

function getListItems(
  props: HeadlineSectionProps,
): SiteTemplatePageSectionTypeHeadlineDefaultListItemSettingModel[] {
  return props.lists.Default ?? [];
}

function getSectionImageUrl(props: HeadlineSectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(
  props: HeadlineSectionProps,
  fallback: string,
): string {
  return props.setting.Image?.AltText ?? fallback;
}

function getItemImageUrl(
  item?: SiteTemplatePageSectionTypeHeadlineDefaultListItemSettingModel | null,
): string | undefined {
  return item?.Image?.Url ?? undefined;
}

function getItemImageAlt(
  item:
    SiteTemplatePageSectionTypeHeadlineDefaultListItemSettingModel | undefined,
  fallback: string,
): string {
  return item?.Image?.AltText ?? item?.Title ?? fallback;
}

const generatedHeadlineRenderers: Record<string, GeneratedHeadlineRenderer> = {
  [HeadlineAppearance.Home14.key]: (props) => {
    const items = getListItems(props);

    return (
      <HomeFourteenTreatmentSectionGenerated
        {...buildGeneratedSectionProps<HomeFourteenTreatmentSectionGeneratedProps>(
          "HomeFourteenTreatmentSection",
          {
            sectionDiv0H20H2Text: props.setting.Title ?? undefined,
            sectionDiv0H20Span1SpanText: props.setting.Subtitle ?? undefined,
            sectionDiv0H20Span3SpanText: props.setting.ButtonText ?? undefined,
            sectionDiv0H20Span0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(props) ?? undefined,
            sectionDiv0H20Span0ImageWithBasePath0ImageAlt: getSectionImageAlt(
              props,
              "headline",
            ),
            sectionDiv0H20Span2ImageWithBasePath0ImageSrc:
              getItemImageUrl(items[0]) ?? undefined,
            sectionDiv0H20Span2ImageWithBasePath0ImageAlt: getItemImageAlt(
              items[0],
              "headline",
            ),
            treatmentSection6s: items.map((item) => ({
              key: item.Id,
              draggableSlider0AfterImage:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/about/slide-01.jpg",
              draggableSlider0BeforeImage:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/about/slide-lg-01.jpg",
              alt: getItemImageAlt(item, "treatment"),
            })),
          },
        )}
      />
    );
  },
  [HeadlineAppearance.Home17.key]: (props) => (
    <HomeSeventeenPreviewSectionGenerated
      {...buildGeneratedSectionProps<HomeSeventeenPreviewSectionGeneratedProps>(
        "HomeSeventeenPreviewSection",
        {
          sectionDiv0H20H2Text: props.setting.Title ?? undefined,
          sectionDiv0H20Span0SpanText: props.setting.Subtitle ?? undefined,
          sectionImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionImageWithBasePath0ImageAlt: getSectionImageAlt(
            props,
            "headline",
          ),
          sectionImageWithBasePath1ImageSrc:
            getItemImageUrl(getListItems(props)[0]) ?? undefined,
          sectionImageWithBasePath1ImageAlt: getItemImageAlt(
            getListItems(props)[0],
            "headline",
          ),
        },
      )}
    />
  ),
  [HeadlineAppearance.Home18.key]: (props) => {
    const items = getListItems(props);

    return (
      <HomeEighteenReadySectionGenerated
        {...buildGeneratedSectionProps<HomeEighteenReadySectionGeneratedProps>(
          "HomeEighteenReadySection",
          {
            sectionDiv0A0AHref: props.setting.ButtonLink ?? undefined,
            sectionDiv0A0AText: props.setting.ButtonText ?? undefined,
            sectionDiv0H20H2Text: props.setting.Title ?? undefined,
            sectionDiv0H20Span0SpanText: props.setting.Subtitle ?? undefined,
            sectionDiv0ImageWithBasePath0ImageSrc:
              getSectionImageUrl(props) ?? undefined,
            sectionDiv0ImageWithBasePath0ImageAlt: getSectionImageAlt(
              props,
              "headline",
            ),
            readySectionServicesSlides: items.map((item) => ({
              key: item.Id,
              h30Text: item.Title ?? "",
            })),
            readySectionServicesSlides2: items.map((item) => ({
              key: item.Id,
              src:
                getItemImageUrl(item) ??
                "/Templates/Doccure/img/company/company-eighteen-1.svg",
              alt: getItemImageAlt(item, "headline"),
            })),
          },
        )}
      />
    );
  },
};

const GeneratedHeadlineSection: FC<HeadlineSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedHeadlineRenderers,
    HeadlineAppearance.Home14.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedHeadlineSection;
